import { useMutation } from '@tanstack/react-query';
import {
  formatDecimal,
  formatMoney,
  lineDiscountError,
  LICENCE_TYPE_CODE,
  parseDecimal,
  type QuoteLineResponse,
  type QuoteLookups,
  type QuoteResponse,
  quoteLineSchema,
  quoteTotals,
  typeTree,
} from '@k-management/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useCan } from '../../auth/AuthProvider.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';

interface Draft {
  typeCode: string;
  subTypeCode: string;
  description: string;
  /** How many times the line is repeated (2.49). */
  editions: string;
  quantity: string;
  unitCode: string;
  unitPrice: string;
  discountPercent: string;
  /** Amount of the line written by hand; empty when the percentage is used. */
  finalAmount: string;
  vatCode: string;
  recurrence: string;
}

/**
 * One line of a quote, filled in step by step (decision of 23/09/2026): the
 * type, its sub-type when it has one, a free text for the voices «Altro», then
 * how much, how it is counted and at which rate.
 */
export function LineDialog({
  quote,
  lookups,
  line,
  onClose,
  onSaved,
  onLicences,
  onFill,
}: {
  quote: QuoteResponse;
  lookups: QuoteLookups;
  /** The line to change; absent to add a new one. */
  line?: QuoteLineResponse;
  onClose: () => void;
  onSaved: (updated: QuoteResponse) => Promise<void>;
  /** Choosing «Licenze» hands over to the licence configurator. */
  onLicences: () => void;
  /** Choosing a type filled from its list hands over to «Compila da listino». */
  onFill: (typeCode: string) => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const canDiscount = useCan('preventivatore.discounts');
  const tree = typeTree(lookups.types);
  const defaultUnit = lookups.units.find((unit) => unit.isDefault)?.code ?? lookups.units[0]?.code;
  const defaultVat =
    lookups.vatRates.find((rate) => rate.isDefault)?.code ?? lookups.vatRates[0]?.code;

  const [draft, setDraft] = useState<Draft>({
    typeCode: line?.typeCode ?? '',
    subTypeCode: line?.subTypeCode ?? '',
    description: line?.description ?? '',
    editions: line ? String(line.editions) : '1',
    quantity: line ? String(Number(line.quantity)) : '1',
    unitCode: line?.unitCode ?? defaultUnit ?? '',
    unitPrice: line?.unitPrice ?? '',
    discountPercent: line ? String(Number(line.discountPercent)) : '0',
    finalAmount: line?.finalAmount ?? '',
    vatCode: line?.vatCode ?? defaultVat ?? '',
    recurrence: line?.recurrence ?? '',
  });
  // A spesa (2.53): trasferte, vitto, rimborsi; out of the margin.
  const [isExpense, setIsExpense] = useState(line?.isExpense ?? false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const [discountReset, setDiscountReset] = useState(false);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const set = (key: keyof Draft, value: string) =>
    setDraft((current) => ({ ...current, [key]: value }));

  /**
   * New editions, quantity or price reset the discount: it was worked out on
   * the old amount, the user writes it again (decision of 26/09/2026).
   */
  const setAmount = (key: 'editions' | 'quantity' | 'unitPrice', value: string) => {
    const changed = parseDecimal(value) !== parseDecimal(draft[key]);
    const discounted =
      draft.finalAmount.trim() !== '' || (parseDecimal(draft.discountPercent) ?? 0) !== 0;
    if (!changed || !discounted) {
      set(key, value);
      return;
    }
    setDiscountReset(true);
    setDraft({ ...draft, [key]: value, discountPercent: '0', finalAmount: '' });
  };
  const byHand = draft.finalAmount.trim() !== '';

  const children = tree.find((item) => item.type.code === draft.typeCode)?.children ?? [];
  const chosen =
    children.length > 0
      ? lookups.types.find((type) => type.code === draft.subTypeCode)
      : lookups.types.find((type) => type.code === draft.typeCode);
  const needsText = chosen?.freeText === true;

  // The price of the list of the quote (proposal Listini §5): the sub-type first, then the whole type.
  const listed =
    quote.activityPrices.find(
      (item) =>
        item.typeCode === draft.typeCode &&
        item.unitCode === draft.unitCode &&
        item.subTypeCode === (draft.subTypeCode || null),
    ) ??
    quote.activityPrices.find(
      (item) =>
        item.typeCode === draft.typeCode &&
        item.unitCode === draft.unitCode &&
        item.subTypeCode === null,
    );
  const listedPrice = listed?.price ?? null;
  // The price proposed last: replaced by the new one, unless the user wrote another.
  const proposed = useRef<string | null>(line?.listPrice ?? null);
  useEffect(() => {
    if (line && proposed.current === null && listedPrice === null) return;
    // The updater runs later: it compares with the price proposed until now.
    const previous = proposed.current;
    proposed.current = listedPrice;
    setDraft((current) => {
      const untouched =
        current.unitPrice.trim() === '' ||
        (previous !== null && parseDecimal(current.unitPrice) === parseDecimal(previous));
      return untouched && listedPrice !== null ? { ...current, unitPrice: listedPrice } : current;
    });
  }, [listedPrice, line]);
  const priceChanged =
    listedPrice !== null && parseDecimal(draft.unitPrice) !== parseDecimal(listedPrice);

  /** Choosing a type resets what depends on it and proposes its unit. */
  const chooseType = (code: string) => {
    // The licences are worked out on the price list, never typed by hand.
    if (code === LICENCE_TYPE_CODE) {
      onLicences();
      return;
    }
    const type = lookups.types.find((item) => item.code === code);
    // A new line of a type filled from its list opens the grid of its sub-types.
    if (!line && type?.fromList) {
      onFill(code);
      return;
    }
    setDraft((current) => ({
      ...current,
      typeCode: code,
      subTypeCode: '',
      description: '',
      unitCode: type?.unit ?? current.unitCode,
    }));
  };
  const chooseSubType = (code: string) => {
    const type = lookups.types.find((item) => item.code === code);
    setDraft((current) => ({
      ...current,
      subTypeCode: code,
      description: '',
      unitCode: type?.unit ?? current.unitCode,
    }));
  };

  const save = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest<QuoteResponse>(
        line ? `/quotes/${quote.id}/lines/${line.id}` : `/quotes/${quote.id}/lines`,
        { method: line ? 'PATCH' : 'POST', body },
      ),
    onSuccess: (updated) => onSaved(updated),
    onError: (error: Error) => showServerError(error, setErrors, setFormError),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError('');
    const { data, errors: found } = validate(quoteLineSchema, {
      typeCode: draft.typeCode,
      subTypeCode: draft.subTypeCode || null,
      description: draft.description || null,
      editions: draft.editions || 1,
      quantity: draft.quantity,
      unitCode: draft.unitCode,
      unitPrice: draft.unitPrice,
      discountPercent: byHand ? 0 : draft.discountPercent || 0,
      finalAmount: byHand ? draft.finalAmount : null,
      vatCode: draft.vatCode,
      recurrence: draft.recurrence || null,
      isExpense,
    });
    setErrors(found);
    if (!data) return;
    if (children.length > 0 && !draft.subTypeCode) {
      setErrors({ subTypeCode: 'Campo obbligatorio' });
      return;
    }
    if (needsText && !draft.description.trim()) {
      setErrors({ description: 'Campo obbligatorio' });
      return;
    }
    if (finalError) {
      setErrors({ finalAmount: finalError });
      return;
    }
    save.mutate(data);
  };

  // What the line is worth while it is being written.
  const previewLine = {
    editions: Math.max(1, Math.trunc(parseDecimal(draft.editions) ?? 1)),
    quantity: number(draft.quantity),
    unitPrice: number(draft.unitPrice),
    discountPercent: byHand ? '0' : number(draft.discountPercent),
    finalAmount: byHand && parseDecimal(draft.finalAmount) !== null ? draft.finalAmount : null,
    vatCode: draft.vatCode,
  };
  const preview = quoteTotals(
    [previewLine],
    Object.fromEntries(lookups.vatRates.map((rate) => [rate.code, rate.rate])),
  );
  const gross = parseDecimal(preview.lines[0]!.gross) ?? 0;
  const net = parseDecimal(preview.net) ?? 0;
  const finalError = byHand ? lineDiscountError(previewLine) : null;
  // The other field shows, in grey, what the one written is worth.
  const percentHint =
    byHand && gross > 0 ? formatDecimal(((gross - net) / gross) * 100, 0, 2) : '0';
  const finalHint = formatDecimal(net, 2, 2);

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <h2>{line ? 'Modifica riga' : 'Nuova riga'}</h2>
        <Field label="Tipologia" error={errors.typeCode}>
          <select value={draft.typeCode} onChange={(event) => chooseType(event.target.value)}>
            <option value="">Scegli…</option>
            {tree.map(({ type }) => (
              <option key={type.code} value={type.code}>
                {type.description}
              </option>
            ))}
          </select>
        </Field>
        {children.length > 0 && (
          <Field label="Di che tipo" error={errors.subTypeCode}>
            <select
              value={draft.subTypeCode}
              onChange={(event) => chooseSubType(event.target.value)}
            >
              <option value="">Scegli…</option>
              {children.map((child) => (
                <option key={child.code} value={child.code}>
                  {child.description}
                </option>
              ))}
            </select>
          </Field>
        )}
        {needsText && (
          <Field
            label="Descrizione"
            error={errors.description}
            hint="Si aggiunge alla tipologia scelta, ed è quello che legge il cliente."
          >
            <input
              value={draft.description}
              maxLength={300}
              autoFocus
              onChange={(event) => set('description', event.target.value)}
              placeholder="Workshop sulla sicurezza"
            />
          </Field>
        )}
        <div className="row">
          <Field
            label="Edizioni"
            error={errors.editions}
            hint="Quante volte si ripete la riga: 3 webinar da 1,5 ore."
          >
            <DecimalInput
              className="number"
              decimals={[0, 0]}
              value={draft.editions}
              onChange={(value) => setAmount('editions', value)}
            />
          </Field>
          <Field label="Quantità" error={errors.quantity}>
            <DecimalInput
              className="number"
              decimals={[0, 3]}
              value={draft.quantity}
              onChange={(value) => setAmount('quantity', value)}
            />
          </Field>
          <Field label="Unità" error={errors.unitCode}>
            <select
              value={draft.unitCode}
              onChange={(event) => set('unitCode', event.target.value)}
            >
              {lookups.units.map((unit) => (
                <option key={unit.code} value={unit.code}>
                  {unit.description}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Prezzo unitario"
            error={errors.unitPrice}
            hint={
              listedPrice !== null ? (
                <>
                  Listino: {formatMoney(listedPrice)}
                  {priceChanged && (
                    <>
                      {' '}
                      <span className="pill info">modificato</span>{' '}
                      <button
                        type="button"
                        className="link small"
                        onClick={() => setAmount('unitPrice', listedPrice)}
                      >
                        ↺ prezzo di listino
                      </button>
                    </>
                  )}
                </>
              ) : draft.typeCode ? (
                'Nessun prezzo di listino: scrivilo tu.'
              ) : undefined
            }
          >
            <DecimalInput
              className="number"
              decimals={[2, 2]}
              value={draft.unitPrice}
              onChange={(value) => setAmount('unitPrice', value)}
            />
          </Field>
        </div>
        {discountReset && (
          <p className="notice">
            Hai cambiato quantità o prezzo: lo sconto è stato azzerato, riscrivilo sul nuovo
            importo.
          </p>
        )}
        {canDiscount && (
          <div className="row">
            <Field
              label="Sconto %"
              error={errors.discountPercent}
              hint={byHand ? 'Calcolato dall’importo finale' : undefined}
            >
              <DecimalInput
                className="number"
                decimals={[0, 2]}
                value={byHand ? '' : draft.discountPercent}
                placeholder={percentHint}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, discountPercent: value, finalAmount: '' }))
                }
              />
            </Field>
            <Field
              label="Importo finale"
              error={errors.finalAmount ?? finalError ?? undefined}
              hint={`Senza sconto ${formatMoney(preview.lines[0]!.gross)}`}
            >
              <DecimalInput
                className="number"
                decimals={[2, 2]}
                value={draft.finalAmount}
                placeholder={finalHint}
                onChange={(value) => set('finalAmount', value)}
              />
            </Field>
          </div>
        )}
        <div className="row">
          <Field label="IVA" error={errors.vatCode}>
            <select value={draft.vatCode} onChange={(event) => set('vatCode', event.target.value)}>
              {lookups.vatRates.map((rate) => (
                <option key={rate.code} value={rate.code}>
                  {rate.description}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Importo">
            <output className="line-amount">{formatMoney(preview.net)}</output>
          </Field>
        </div>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={isExpense}
            onChange={(event) => setIsExpense(event.target.checked)}
          />{' '}
          È una spesa (trasferte, vitto, rimborsi): fuori dal margine, controllata a parte
        </label>
        <Field
          label="Ricorrenza"
          error={errors.recurrence}
          hint="Facoltativa: come la legge il cliente nell’offerta («Una tantum», «12 mesi dal… al…»)."
        >
          <input
            value={draft.recurrence}
            maxLength={120}
            onChange={(event) => set('recurrence', event.target.value)}
          />
        </Field>
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? 'Salvataggio…' : 'Salva riga'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** What the user is typing is not always a number yet: the preview stays quiet. */
function number(value: string): string {
  return parseDecimal(value) === null ? '0' : value;
}
